import { groupBy, mapValues } from 'lodash-es'
import { searchIndexes } from '../constants/search.ts'
import { ActivityStatus } from './Activity.ts'
import { JobFeeType } from './Job.ts'
import { BehaviorInNewGroup, ExperienceLevel, ReferralSource, SeniorityLevel } from './MembershipRequest.ts'
import { OrgGovPaidProfileLevel, OrgType } from './Org.ts'
import type { OrgTransaction, OrgTransactionPaymentEntity } from './OrgTransaction.ts'
import { ProjectDisability } from './Project.ts'
import { SkillType } from './Skill.ts'
import { TalentBaseUserProfileStatusType } from './User.ts'
import { VisibilityType } from './visibility.ts'

export type optionType = {
    id: string
    value: string
    label: string
    icon?: string
    description?: string
}

export function mapValuesStr(optionsList: optionType[]) {
    return mapValues(groupBy(optionsList, 'value'), (x) => x[0])
}

export function valueLookup(item: string, list: optionType[], field?: string) {
    const key = field || 'label'
    if (list && item) {
        const option = mapValuesStr(list)
        if (option && option[item]) {
            const result = option[item] as { [index: string]: string }
            if (key in result) {
                return result[key]
            }
        }
    }
    return ''
}

export const orgTransactionTypeDictionary: { [key in OrgTransaction['type']]: string } = {
    'deposit': 'Príjem',
    'payment': 'Výdavok'
}

export const orgTransactionPaymentEntityTypeDictionary: { [key in OrgTransactionPaymentEntity['type']]: string } = {
    'talent_base_user_show': 'Zobrazenie TalentBase profilu',
    'job_fee': 'Promovanie pracovnej ponuky'
}

export const orgGovPaidProfileLevelDictionary: { [key in OrgGovPaidProfileLevel]: string } = {
    [OrgGovPaidProfileLevel.PLUS]: 'Plus',
    [OrgGovPaidProfileLevel.PREMIUM]: 'Premium',
    [OrgGovPaidProfileLevel.PARTNER]: 'Modré z neba'
}
export const orgGovPaidProfileLevels: optionType[] = Object.values(OrgGovPaidProfileLevel).map((id) => ({
    id,
    value: id,
    label: orgGovPaidProfileLevelDictionary[id]
}))

export const orgTypeDictionary: { [key in OrgType]: string } = {
    [OrgType.publicSector]: 'Verejný sektor',
    [OrgType.school]: 'Škola',
    [OrgType.nonProfit]: 'Neziskový sektor',
    [OrgType.startUp]: 'Start-up',
    [OrgType.privateSector]: 'Súkromný sektor',
    [OrgType.other]: 'Projekt / iniciatíva bez právnej formy'
}
export const orgTypes: optionType[] = Object.values(OrgType).map((id) => ({
    id,
    value: id,
    label: orgTypeDictionary[id]
}))

export const orgTypeFormOptions: { value: OrgType; label: string; description: string }[] = [
    {
        value: OrgType.privateSector,
        label: 'Súkromný sektor',
        description: 'startupy, s.r.o., a.s., živnostníci a iné obchodné spoločnosti'
    },
    {
        value: OrgType.nonProfit,
        label: 'Neziskový sektor',
        description:
            'združenia, príspevkové organizácie, zväzy, spolky, kluby, hospice, knižnice, komunitné centrá, ...'
    },
    {
        value: OrgType.publicSector,
        label: 'Verejný sektor',
        description: 'ministerstvá, úrady, samosprávy, školy, štátne inštitúcie'
    },
    {
        value: OrgType.other,
        label: 'Projekt / iniciatíva bez právnej formy',
        description: 'neformálne projekty, občianske iniciatívy, ...'
    }
]
export const orgTypeProfileLinkText = (type: OrgType) =>
    type === OrgType.other ? 'Profil iniciatívy' : 'Profil organizácie'
export const orgTypeProfileAboutText = (type: OrgType) => (type === OrgType.other ? 'O iniciatíve' : 'O organizácii')

export const skillTypeDict: { [key in SkillType]: string } = {
    [SkillType.kurz]: 'Kurz',
    [SkillType.event]: 'Udalosť',
    [SkillType.workgroup]: 'Skupina',
    [SkillType.studyOpportunity]: 'Študijná a štipendijná príležitosť'
}

export const skillTypes: optionType[] = Object.values(SkillType).map((id) => ({
    id,
    value: id,
    label: skillTypeDict[id]
}))

export const skillTypeAboutDict: { [key in SkillType]: string } = {
    [SkillType.kurz]: 'O kurze',
    [SkillType.event]: 'O udalosti',
    [SkillType.workgroup]: 'O skupine',
    [SkillType.studyOpportunity]: 'O príležitosti'
}

export const skillTypesAbout: optionType[] = Object.values(SkillType).map((id) => ({
    id,
    value: id,
    label: skillTypeAboutDict[id]
}))

export const experienceLevelDict: { [key in ExperienceLevel]: string } = {
    [ExperienceLevel.main]: 'oblasť je moja hlavná (pracovná) náplň',
    [ExperienceLevel.marginal]: 'oblasti sa venujem len okrajovo/krátko',
    [ExperienceLevel.interested]: 'oblasť ma zaujíma, ale nemám žiadnu skúsenosť'
}

export const experienceLevels: optionType[] = Object.values(ExperienceLevel).map((id) => ({
    id,
    value: id,
    label: experienceLevelDict[id]
}))

export const behaviorInNewGroupDict: { [key in BehaviorInNewGroup]: string } = {
    [BehaviorInNewGroup.progress]: 'sa na začiatku zapájaš menej, ale časom sa uvoľníš a rozhovoríš',
    [BehaviorInNewGroup.initiate]: 'vždy rozprúdiš rozhovor',
    [BehaviorInNewGroup.passive]: 'skôr len počúvaš ostatných a veľmi sa nezapájaš do rozhovoru',
    [BehaviorInNewGroup.involve]: 'rozhovor nerozprúdiš ty, ale zapojíš sa'
}

export const behaviorInNewGroups: optionType[] = Object.values(BehaviorInNewGroup).map((id) => ({
    id,
    value: id,
    label: behaviorInNewGroupDict[id]
}))

export const seniorityLevel: { [key in SeniorityLevel]: string } = {
    [SeniorityLevel.expert]: 'cítim sa v tom ako ryba vo vode',
    [SeniorityLevel.marginal]: 'venujem sa tomu len okrajovo/krátko',
    [SeniorityLevel.noExperience]: 'nemám žiadnu skúsenosť'
}

export const seniorityLevels: optionType[] = Object.values(SeniorityLevel).map((id) => ({
    id,
    value: id,
    label: seniorityLevel[id]
}))

export const referralSourceDict: { [key in ReferralSource]: string } = {
    [ReferralSource.knownPerson]: 'známy/a alebo kolega/yňa',
    [ReferralSource.socialMedia]: 'sociálne siete',
    [ReferralSource.newsletter]: 'GROWNi newsletter',
    [ReferralSource.website]: 'GROWNi web',
    [ReferralSource.other]: 'iné (napíš):'
}

export const referralSources: optionType[] = Object.values(ReferralSource).map((id) => ({
    id,
    value: id,
    label: referralSourceDict[id]
}))

export const visibilityDict: { [key in VisibilityType]: string } = {
    [VisibilityType.published]: 'Vidia všetci',
    [VisibilityType.unlisted]: 'Prístupné iba cez link',
    [VisibilityType.hidden]: 'Ukončené',
    [VisibilityType.draft]: 'Koncept'
}

export const visibilityTypes: optionType[] = [
    {
        id: VisibilityType.published,
        value: VisibilityType.published,
        label: visibilityDict[VisibilityType.published],
        icon: 'public',
        description: 'Môže si pozrieť každý. Zobrazí sa v zoznamoch a vo výsledkoch vyhľadávania.'
    },
    {
        id: VisibilityType.unlisted,
        value: VisibilityType.unlisted,
        label: visibilityDict[VisibilityType.unlisted],
        icon: 'link',
        description: 'Zobraziť môžu iba tí, ktorí majú link. Nezobrazí sa v zoznamoch ani vo výsledkoch vyhľadávania.'
    },
    {
        id: VisibilityType.hidden,
        value: VisibilityType.hidden,
        label: visibilityDict[VisibilityType.hidden],
        icon: 'lock',
        description:
            'Nezobrazí sa v zoznamoch ani výsledkoch vyhľadávania. V aktivitách bude medzi Ukončenými. Tento stav viete kedykoľvek vrátiť späť. Záujemcovia, ktorí sa už prihlásili, nebudú o zmene notifikovaní.'
    },
    {
        id: VisibilityType.draft,
        value: VisibilityType.draft,
        label: visibilityDict[VisibilityType.draft],
        icon: 'lock',
        description: 'Nezobrazí sa v zoznamoch ani výsledkoch vyhľadávania. V aktivitách bude medzi Konceptmi.'
    }
]

export const visibilityDictProfiles: { [key in VisibilityType]: string } = {
    [VisibilityType.published]: 'Verejné',
    [VisibilityType.unlisted]: 'Nezverejnené',
    [VisibilityType.hidden]: 'Súkromné',
    [VisibilityType.draft]: 'Koncept' // neviem ako to ma byt v zneni profilu
}

export const visibilityTypesProfiles: optionType[] = [
    {
        id: VisibilityType.published,
        value: VisibilityType.published,
        label: visibilityDictProfiles[VisibilityType.published],
        icon: 'public',
        description: 'Môže vidieť každý. Zobrazí sa medzi príležitosťami a vo výsledkoch vyhľadávania.'
    },
    {
        id: VisibilityType.unlisted,
        value: VisibilityType.unlisted,
        label: visibilityDictProfiles[VisibilityType.unlisted],
        icon: 'link',
        description:
            'Profil je prístupný, iba cez link. Nezobrazí sa medzi príležitosťami ani vo výsledkoch vyhľadávania.'
    },
    {
        id: VisibilityType.hidden,
        value: VisibilityType.hidden,
        label: visibilityDictProfiles[VisibilityType.hidden],
        icon: 'lock',
        description: 'Uvidíš len ty, nezobrazí sa medzi príležitosťami ani vo výsledkoch vyhľadávania.'
    }
]

export const activityStatuses: optionType[] = [
    {
        id: ActivityStatus.WAITING,
        value: ActivityStatus.WAITING,
        label: 'V procese'
    },

    {
        id: ActivityStatus.ACTIVE,
        value: ActivityStatus.ACTIVE,
        label: 'Prijatí'
    },

    {
        id: ActivityStatus.FINISHED,
        value: ActivityStatus.FINISHED,
        label: 'Nepostúpili'
    },

    { id: 'all', value: 'all', label: 'Všetci' }
]

export const talentBaseStatusDict: { [key in TalentBaseUserProfileStatusType]: string } = {
    [TalentBaseUserProfileStatusType.activelySeeking]: 'Aktívne hľadám',
    [TalentBaseUserProfileStatusType.offerDependent]: 'Záleží od ponuky',
    [TalentBaseUserProfileStatusType.notSeeking]: 'Nehľadám'
}

export const talentBaseProfileStatus: optionType[] = [
    {
        id: TalentBaseUserProfileStatusType.activelySeeking,
        value: TalentBaseUserProfileStatusType.activelySeeking,
        label: talentBaseStatusDict[TalentBaseUserProfileStatusType.activelySeeking],
        description: 'znamená, že si hľadáš prácu, chceš zmenu kariéry a ideálne čo najskôr.'
    },
    {
        id: TalentBaseUserProfileStatusType.offerDependent,
        value: TalentBaseUserProfileStatusType.offerDependent,
        label: talentBaseStatusDict[TalentBaseUserProfileStatusType.offerDependent],
        description:
            'znamená, že si spokojný tam kde si, ale chceš byť v GROWNi TalentBase pre prípad, že by ťa oslovila organizácia so zaujímavou ponukou.'
    },
    {
        id: TalentBaseUserProfileStatusType.notSeeking,
        value: TalentBaseUserProfileStatusType.notSeeking,
        label: talentBaseStatusDict[TalentBaseUserProfileStatusType.notSeeking],
        description: 'znamená, že sa nebudeš zobrazovať v TalentBase.'
    }
]

// Project

export const PROJECT_DISABILITY_DICT: Record<ProjectDisability, string> = {
    [ProjectDisability.VISUAL]: 'so zrakovým znevýhodnením',
    [ProjectDisability.HEARING]: 'so sluchovým znevýhodnením',
    [ProjectDisability.MOBILITY]: 's pohybovým obmedzením',
    [ProjectDisability.COMMUNICATION]: 's narušenou komunikačnou schopnosťou',
    [ProjectDisability.SOCIAL]: 'zo sociálne znevýhodneného prostredia',
    [ProjectDisability.INTELLECTUAL]: 's mentálnym znevýhodnením',
    [ProjectDisability.MENTAL]: 's duševným ochorením'
}

export const PROJECT_DISABILITY_OPTIONS: optionType[] = Object.values(ProjectDisability).map((projectDisability) => ({
    id: projectDisability,
    value: projectDisability,
    label: PROJECT_DISABILITY_DICT[projectDisability]
}))

export const JOB_FEE_TYPE_DICT: Record<JobFeeType, string> = {
    [JobFeeType.SATISFACTION]: 'Satisfaction fee',
    [JobFeeType.ADVERTISING]: 'Inzertné fee'
}

// Search index

export const SEARCH_INDEX_DICT: Record<searchIndexes, string> = {
    [searchIndexes.TAGS]: 'Klúčové slová',
    [searchIndexes.JOBS]: 'Pracovné ponuky',
    [searchIndexes.ORGS]: 'Organizácie',
    [searchIndexes.MENTORS]: 'Mentori',
    [searchIndexes.PROJECTS]: 'Dobrovoľnícke príležitosti',
    [searchIndexes.SKILLS]: 'Vzdelávacie príležitosti',
    [searchIndexes.VOLUNTEERS]: 'Dobrovoľníci',
    [searchIndexes.COMMUNITY_MEMBERS]: 'Členovia komunity',
    [searchIndexes.COMMUNITY_GROUPS]: 'Skupiny komunity',
    [searchIndexes.COMMUNITY_SECTIONS]: 'Sekcie komunity',
    [searchIndexes.TALENTBASE_MEMBERS]: 'Členovia TalentBase',
    [searchIndexes.JOB_REQUESTS]: 'Žiadosti o prácu',
    [searchIndexes.USERS]: 'Používatelia',

    // Not used
    [searchIndexes.SKILLS_START_DATE_DESC_INDEX]: '',
    [searchIndexes.SKILLS_START_DATE_ASC_INDEX]: '',
    [searchIndexes.TAGS_PUBLISHED_UNLISTED]: '',
    [searchIndexes.JOBS_PUBLISHED_UNLISTED]: '',
    [searchIndexes.MENTORS_PUBLISHED_UNLISTED]: '',
    [searchIndexes.PROJECTS_PUBLISHED_UNLISTED]: '',
    [searchIndexes.SKILLS_PUBLISHED_UNLISTED]: '',
    [searchIndexes.VOLUNTEERS_PUBLISHED_UNLISTED]: '',
    [searchIndexes.SEARCHABLE_PAGES_DATA]: '',
    [searchIndexes.MERGED_OPPORTUNITIES]: '',
    [searchIndexes.BLOGS]: ''
}

export const SEARCH_INDEX_NAME_DICT: Record<string, string> = {
    [searchIndexes.TAGS]: 'Klúčové slová',
    [searchIndexes.JOBS]: 'Práca',
    [searchIndexes.ORGS]: 'Organizácie',
    [searchIndexes.MENTORS]: 'Mentori',
    [searchIndexes.PROJECTS]: 'Dobrovoľnícke príležitosti',
    [searchIndexes.SKILLS]: 'Vzdelávanie',
    [searchIndexes.VOLUNTEERS]: 'Dobrovoľníci',
    [searchIndexes.COMMUNITY_MEMBERS]: 'Členovia komunity',
    [searchIndexes.TALENTBASE_MEMBERS]: 'Členovia TalentBase',
    [searchIndexes.USERS]: 'Používatelia',
    [searchIndexes.SEARCHABLE_PAGES_DATA]: 'Stránky',
    [searchIndexes.BLOGS]: 'Blogy',

    // Typesense collections (used by global search)
    'mentors': 'Mentori',

    // Not used
    [searchIndexes.SKILLS_START_DATE_DESC_INDEX]: '',
    [searchIndexes.SKILLS_START_DATE_ASC_INDEX]: '',
    [searchIndexes.TAGS_PUBLISHED_UNLISTED]: '',
    [searchIndexes.JOBS_PUBLISHED_UNLISTED]: '',
    [searchIndexes.MENTORS_PUBLISHED_UNLISTED]: '',
    [searchIndexes.PROJECTS_PUBLISHED_UNLISTED]: '',
    [searchIndexes.SKILLS_PUBLISHED_UNLISTED]: '',
    [searchIndexes.VOLUNTEERS_PUBLISHED_UNLISTED]: '',
    [searchIndexes.MERGED_OPPORTUNITIES]: ''
}
